import { Smartphone } from 'lucide-react';
import { useEffect, useState } from 'react';
import type { BusinessDetailsView } from '@quds/shared';
import { EmptyState, LoadingState, Modal } from '@/components/ui';
import { getBusinessDetails } from '@/data';
import { OwnerUsersManager } from '@/features/pharmacies/owners/OwnerUsersManager';
import type { ActionModalProps } from '../types';

/** Thin modal host — Owner management UI is shared with the Business workspace Owner tab. */
export function MobileAccessModal({ pharmacy, onClose }: ActionModalProps) {
  const [details, setDetails] = useState<BusinessDetailsView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const view = await getBusinessDetails(pharmacy.id);
      setDetails(view);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Could not load owner accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, [pharmacy.id]);

  return (
    <Modal
      open
      onClose={onClose}
      title="Owner Users"
      description={pharmacy.name}
      icon={<Smartphone size={18} aria-hidden="true" />}
      size="xl"
    >
      {loading && !details && <LoadingState className="py-10" label="Loading Owner Users…" />}
      {loadError && !details && <EmptyState title="Could not load owners" description={loadError} />}
      {details && (
        <OwnerUsersManager
          businessId={pharmacy.id}
          businessName={pharmacy.name}
          details={details}
          onRefresh={reload}
        />
      )}
    </Modal>
  );
}
